import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  AlertTriangle,
  ArrowRight,
  Ban,
  Banknote,
  FileClock,
  Loader2,
  Plus,
  Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { compact, formatDate, money, thisMonth } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { PortalActivity } from "@/components/PortalActivity";
import { useAuth } from "@/context/AuthContext";

const KPI = ({ label, value, hint, icon: Icon, tone = "ink", testId, onClick }) => {
  const body = (
    <>
      <div className="flex items-start justify-between">
        <p className="label-caps">{label}</p>
        <Icon
          className={`h-4 w-4 ${tone === "danger" ? "text-red-500" : tone === "good" ? "text-emerald-600" : "text-slate-400"}`}
          strokeWidth={1.6}
        />
      </div>
      <p className="num mt-4 text-2xl tracking-tight text-slate-900">{value}</p>
      <p className="mt-1.5 text-[11px] text-slate-500">{hint}</p>
    </>
  );
  if (!onClick) {
    return (
      <div className="surface p-5" data-testid={testId}>
        {body}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="surface group p-5 text-left transition-all duration-200 hover:-translate-y-0.5 hover:border-brand-terracotta hover:shadow-[0_6px_20px_-12px_rgba(51,31,38,0.45)]"
    >
      {body}
      <span className="mt-3 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 transition-colors duration-150 group-hover:text-brand-terracotta">
        See fee notes
        <ArrowRight className="h-3 w-3" strokeWidth={2} />
      </span>
    </button>
  );
};

export default function Dashboard() {
  const { currency, loadSettings } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: payload } = await api.get("/reports/dashboard");
      setData(payload);
    } catch (error) {
      toast.error(errorMessage(error, "Could not load dashboard"));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const drill = (params) => {
    const query = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== ""),
    );
    navigate(`/invoices?${query.toString()}`);
  };

  const seed = async () => {    setSeeding(true);
    try {
      await api.post("/demo/seed");
      await loadSettings();
      await load();
      toast.success("Sample chambers data loaded");
    } catch (error) {
      toast.error(errorMessage(error, "Could not load sample data"));
    } finally {
      setSeeding(false);
    }
  };

  if (!data) {
    return (
      <div className="flex h-64 items-center justify-center" data-testid="dashboard-loading">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }

  const { kpis, aging, monthly, recent, top_debtors: debtors, counts } = data;
  const symbol = data.currency_symbol || currency;
  const empty = kpis.invoice_count === 0;

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      <header className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="label-caps">Chambers overview</p>
          <h1 className="mt-3 font-serif text-3xl tracking-tight text-slate-900 lg:text-4xl">
            Receivables desk
          </h1>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/invoices/new"
            data-testid="create-invoice-button"
            className="inline-flex items-center gap-2 bg-slate-900 px-5 py-2.5 text-[13px] font-semibold text-white transition-colors duration-150 hover:bg-slate-800"
          >
            <Plus className="h-4 w-4" strokeWidth={2} />
            New fee note
          </Link>
          <Link
            to="/reports"
            className="inline-flex items-center gap-2 border border-slate-300 bg-white px-5 py-2.5 text-[13px] font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-50"
            data-testid="dashboard-reports-link"
          >
            Aging &amp; ledger
            <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.8} />
          </Link>
        </div>
      </header>

      {empty ? (
        <div className="surface hairline-grid p-10 text-center" data-testid="dashboard-empty-state">
          <h2 className="font-serif text-2xl text-slate-900">Your ledger is empty</h2>
          <p className="mx-auto mt-3 max-w-md text-sm text-slate-600">
            Add a law firm, its instructing lawyers and a matter — or load a sample chambers
            file to see the billing engine end to end.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={seed}
              disabled={seeding}
              data-testid="seed-demo-button"
              className="inline-flex items-center gap-2 bg-slate-900 px-5 py-2.5 text-[13px] font-semibold text-white transition-colors duration-150 hover:bg-slate-800 disabled:opacity-60"
            >
              {seeding && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Load sample chambers data
            </button>
            <button
              type="button"
              onClick={() => navigate("/firms")}
              className="border border-slate-300 bg-white px-5 py-2.5 text-[13px] font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-50"
              data-testid="empty-add-firm-button"
            >
              Add a law firm
            </button>
          </div>
        </div>
      ) : null}

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <KPI
          label="Outstanding"
          value={compact(kpis.outstanding, symbol)}
          hint={`${(counts.sent || 0) + (counts.overdue || 0)} invoice(s) awaiting payment`}
          icon={Wallet}
          testId="kpi-outstanding"
          onClick={() => drill({ status: "open" })}
        />
        <KPI
          label="Overdue"
          value={compact(kpis.overdue, symbol)}
          hint={`${counts.overdue || 0} past the due date`}
          icon={AlertTriangle}
          tone="danger"
          testId="kpi-overdue"
          onClick={() => drill({ status: "overdue" })}
        />
        <KPI
          label="Collected this month"
          value={compact(kpis.collected_month, symbol)}
          hint={`Billed ${compact(kpis.billed_month, symbol)} this month`}
          icon={Banknote}
          tone="good"
          testId="kpi-collected"
          onClick={() => drill({ paid_month: thisMonth() })}
        />
        <KPI
          label="In draft"
          value={compact(kpis.draft_value, symbol)}
          hint={`${counts.draft || 0} unissued fee note(s)`}
          icon={FileClock}
          testId="kpi-draft"
          onClick={() => drill({ status: "draft" })}
        />
        <KPI
          label="Written off"
          value={compact(kpis.written_off || 0, symbol)}
          hint={`${counts.written_off || 0} fee note(s) given up on`}
          icon={Ban}
          testId="kpi-written-off"
          onClick={() => drill({ status: "written_off" })}
        />
      </section>

      <section className="grid gap-4 lg:grid-cols-3">
        <div className="surface min-w-0 overflow-hidden p-6 lg:col-span-2" data-testid="monthly-chart">
          <div className="flex items-baseline justify-between">
            <h2 className="font-serif text-xl text-slate-900">Billed vs collected</h2>
            <p className="label-caps">Last 6 months</p>
          </div>
          <div className="mt-6 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthly} margin={{ left: -18, right: 8, top: 8 }}>
                <CartesianGrid stroke="#F5EAE8" vertical={false} />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 11, fill: "#8C6A6C" }}
                  axisLine={{ stroke: "#EBD9D6" }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#8C6A6C" }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => (v >= 100000 ? `${v / 100000}L` : v / 1000 + "k")}
                />
                <Tooltip
                  formatter={(value) => money(value, symbol)}
                  contentStyle={{
                    border: "1px solid #EBD9D6",
                    borderRadius: 2,
                    fontSize: 12,
                    fontFamily: "Manrope",
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="billed"
                  stroke="#331F26"
                  strokeWidth={2}
                  dot={{ r: 3, fill: "#331F26" }}
                />
                <Line
                  type="monotone"
                  dataKey="collected"
                  stroke="#3D7355"
                  strokeWidth={2}
                  strokeDasharray="4 3"
                  dot={{ r: 3, fill: "#3D7355" }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 flex gap-6 text-[11px] uppercase tracking-[0.14em] text-slate-500">
            <span className="flex items-center gap-2">
              <span className="h-0.5 w-5 bg-slate-900" /> Billed
            </span>
            <span className="flex items-center gap-2">
              <span className="h-0.5 w-5 bg-emerald-600" /> Collected
            </span>
          </div>
        </div>

        <div className="surface min-w-0 overflow-hidden p-6" data-testid="aging-chart">
          <h2 className="font-serif text-xl text-slate-900">Aging of receivables</h2>
          <div className="mt-6 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={aging} margin={{ left: -20, right: 8, top: 8 }}>
                <CartesianGrid stroke="#F5EAE8" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 9, fill: "#8C6A6C" }}
                  axisLine={{ stroke: "#EBD9D6" }}
                  tickLine={false}
                  interval={0}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#8C6A6C" }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => (v >= 100000 ? `${v / 100000}L` : v / 1000 + "k")}
                />
                <Tooltip
                  formatter={(value) => money(value, symbol)}
                  contentStyle={{
                    border: "1px solid #EBD9D6",
                    borderRadius: 2,
                    fontSize: 12,
                    fontFamily: "Manrope",
                  }}
                />
                <Bar dataKey="amount" barSize={26} cursor="pointer"
                  onClick={(entry) => entry?.payload?.bucket && drill({ bucket: entry.payload.bucket })}
                >
                  {aging.map((entry) => (
                    <Cell
                      key={entry.bucket}
                      fill={
                        entry.bucket === "current"
                          ? "#B08C89"
                          : entry.bucket === "d90_plus"
                            ? "#B0402C"
                            : "#331F26"
                      }
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="mt-3 text-[11px] text-slate-500">
            Tap a bar to list the fee notes sitting in that bucket.
          </p>
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-3">
        <div className="surface min-w-0 lg:col-span-2" data-testid="recent-invoices">
          <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
            <h2 className="font-serif text-xl text-slate-900">Recent fee notes</h2>
            <Link
              to="/invoices"
              className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 transition-colors duration-150 hover:text-slate-900"
              data-testid="view-all-invoices-link"
            >
              View all
            </Link>
          </div>
          <div className="divide-y divide-slate-100">
            {recent.length === 0 && (
              <p className="px-6 py-8 text-sm text-slate-500">No invoices yet.</p>
            )}
            {recent.map((inv) => (
              <Link
                key={inv.invoice_id}
                to={`/invoices/${inv.invoice_id}`}
                data-testid={`recent-invoice-${inv.invoice_number}`}
                className="flex items-center justify-between gap-4 px-6 py-4 transition-colors duration-150 hover:bg-slate-50"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-3">
                    <span className="num text-[13px] text-slate-900">{inv.invoice_number}</span>
                    <StatusBadge status={inv.display_status} testId={`recent-status-${inv.invoice_number}`} />
                  </div>
                  <p className="mt-1 truncate text-[13px] text-slate-600">{inv.case_title}</p>
                  <p className="truncate text-[11px] text-slate-400">
                    {inv.contact_name}
                    {inv.firm_name ? ` · ${inv.firm_name}` : ""}
                  </p>
                </div>
                <div className="text-right">
                  <p className="num text-[13px] text-slate-900">{money(inv.total, symbol)}</p>
                  <p className="mt-1 text-[11px] text-slate-400">Due {formatDate(inv.due_date)}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="surface min-w-0" data-testid="top-debtors">
          <div className="border-b border-slate-200 px-6 py-4">
            <h2 className="font-serif text-xl text-slate-900">Who owes most</h2>
          </div>
          <div className="divide-y divide-slate-100">
            {debtors.length === 0 && (
              <p className="px-6 py-8 text-sm text-slate-500">Nothing outstanding.</p>
            )}
            {debtors.map((d) => (
              <button
                key={d.name}
                type="button"
                onClick={() =>
                  drill(
                    d.firm_id
                      ? { firm_id: d.firm_id, status: "open" }
                      : d.client_id
                        ? { client_id: d.client_id, status: "open" }
                        : { status: "open", q: d.name },
                  )
                }
                data-testid={`debtor-row-${d.name}`}
                className="flex w-full items-center justify-between px-6 py-4 text-left transition-colors duration-150 hover:bg-slate-50"
              >
                <div className="min-w-0">
                  <p className="truncate text-[13px] text-slate-800">{d.name}</p>
                  <p className="text-[11px] text-slate-400">{d.count} open invoice(s)</p>
                </div>
                <p className="num text-[13px] text-slate-900">{compact(d.amount, symbol)}</p>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-3">
        <div className="min-w-0 lg:col-span-2">
          <PortalActivity symbol={symbol} />
        </div>
      </section>
    </div>
  );
}
